import React, { useState, useRef, useEffect, useContext } from "react";
import { Text, Spin, Tooltip } from "react_reusable_components";
import { useIntl } from "react-intl";

import { TwoColumn, TwoRow } from "../../../core/layouts";
import Graph from "../DashboardGraphs/Graphs/Grading/Graph";
import {
  getConditionalContent,
  getDashboardQueryParams,
  getLocalizedMessage,
} from "../../../UtilityFunctions/utils";
import variables from "../../../themes/base/styles/variables";
import TableActions from "../../../components/TableActions";
import DashboardService from "../../../services/dashboard-service";
import { DashboardContext } from "../../../globalContext/dashboard/dashboardProvider";
import { GlobalLoaderContext } from "../../../globalContext/globalLoader/globalLoaderProvider";
import { BAD_REQUEST } from "../../../constants/errorConstants";
import useResponsive from "../../../core/hooks/useResponsive";
import { InfoCircleOutlined } from "@ant-design/icons";
import { GRAPH_TOOLTIP, userRole } from "../../../constants";
import GraphTooltipContainer from "../../GraphTooltipContainer";
import { AuthContext } from "../../../globalContext/auth/authProvider";

export const GradingGraphStyles = {
  gradingBox: {
    border: variables.border,
    padding: variables.paddingSmall,
    backgroundColor: variables.bodyBg,
    minHeight: variables.graphsMinHeight,
  },
  headerSection: {
    justifyContent: variables.spaceBetween,
  },
  headerButtons: {
    gap: variables.gapMediumSmall,
    paddingTop: variables.paddingSmallest,
  },
  heading: {
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeXlarge,
  },
  bottomSection: {
    paddingTop: variables.paddingSmall,
    // height: variables.spinnerAndChooseFilterTextHeight,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    display: variables.dFlex,
  },
  spinner: {
    justifyContent: variables.alignCenter,
    paddingTop: variables.paddingTop,
  },
  notFoundText: {
    fontSize: variables.fontSizeXlarge,
    textAlign: variables.alignCenter,
    width: variables.fullWidth,
  },
  tooltipWidth: { width: variables.tooltipWidth },
};

const GradingGraph = ({ filter }) => {// NOSONAR
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardState, dashboardDispatch] = useContext(DashboardContext);
  const [gradingGraphData, setGradingGraph] = useState([]);
  const [authState] = useContext(AuthContext);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [dataNotFound, setDataNotFound] = useState("");
  const screenWidth = window.innerWidth;
  const responsive = useResponsive();

  useEffect(() => {
    let params = getDashboardQueryParams(filter);
    delete params.isPercentage;
    if (
      !params.year ||
      (authState?.currentRole?.id === userRole.ADMIN && !params.countryId) ||
      !params.type ||
      (params.type === "Repair" && !params.subProductCategoryId)
    ) {
      setDataNotFound(
        getLocalizedMessage(
          intl,
          authState?.currentRole?.id !== userRole.ADMIN
            ? "selectCategories.CA.TM.label"
            : "selectCategories.label"
        )
      );
    } else {
      if (dataNotFound) {
        setDataNotFound("");
      }
      DashboardService.getGradingGraph({
        dispatch: dashboardDispatch,
        intl,
        params,
      }).catch((error) => {
        if (error?.response?.data?.code === BAD_REQUEST) {
          setDataNotFound(getLocalizedMessage(intl, "dataNotAvailable.label"));
        }
      });
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (dashboardState.gradingGraph) {
      const newData = dashboardState.gradingGraph;
      const data = [];
      for (const key in newData) {
        data.push({
          name: key,
          value: newData[key],
        });
      }
      setGradingGraph(data);
    }
  }, [dashboardState]);

  const yAxisTicks = [0, 100, 200, 300, 400];

  const yAxisLabelStyle = {
    fontSize: variables.fontSizeSmallest,
    fontWeight: variables.fontFamilyBold,
    fill: variables.textDefault,
  };

  const [graphMounted, setGraphMounted] = useState(false);

  // Use a useRef to access the Graph component
  const graphRef = useRef();

  useEffect(() => {
    setGraphMounted(true);
  }, []);

  const downloadChartImage = () => {
    // Call the downloadChartImage function of the Graph component only if it is mounted
    try {
      if (graphMounted && graphRef.current) {
        graphRef.current.downloadChartImage();
      }
    } catch (error) {
      setDataNotFound(error);
    }
  };

  const downloadUserReportsHandler = () => {
    let queryParams = getDashboardQueryParams(filter);
    delete queryParams.isPercentage;
    DashboardService.dashboardDownloadReportApiCall({
      params: {
        ...queryParams,
        fileName: getLocalizedMessage(intl, "dashboard.gradingGraph.label"),
        reportType: getLocalizedMessage(intl, "gradingReport.label"),
      },
      intl,
      loaderDispatch: loaderDispatch,
    });
  };

  return (
    <TwoRow
      id="grading-graph"
      style={GradingGraphStyles.gradingBox}
      topSection={
        <TwoRow
          topSection={
            <TwoColumn
              style={GradingGraphStyles.headerSection}
              leftSection={
                <Text type="secondary" style={GradingGraphStyles.heading}>
                  {getLocalizedMessage(intl, "dashboard.gradingGraph.label")}{" "}
                  <Tooltip
                    overlayInnerStyle={GradingGraphStyles.tooltipWidth}
                    title={
                      <GraphTooltipContainer
                        data={GRAPH_TOOLTIP(intl).GRADING_GRAPH}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Text>
              }
              rightSection={
                dataNotFound || !gradingGraphData?.length ? null : (
                  <TableActions
                    tableActionsOptions={[
                      {
                        label: getLocalizedMessage(intl, "downloadGraph.label"),
                        action: downloadChartImage,
                      },
                      {
                        label: getLocalizedMessage(
                          intl,
                          "downloadReport.label"
                        ),
                        action: downloadUserReportsHandler,
                      },
                    ]}
                  />
                )
              }
            />
          }
        />
      }
      bottomSectionStyle={getConditionalContent({
        condition: isLoading,
        trueValue: GradingGraphStyles.spinner,
        falseValue: GradingGraphStyles.bottomSection,
      })}
      bottomSection={
        isLoading ? (
          <Spin size={"large"} type={"default"} />
        ) : !dataNotFound && gradingGraphData?.length ? (
          <Graph
            ref={graphRef}
            id="graph"
            data={gradingGraphData}
            yAxisTicks={yAxisTicks}
            label={{
              value: getLocalizedMessage(intl, "gradingGraph.labelValue"),
              angle: -90,
              position: "insideLeft",
              style: yAxisLabelStyle,
            }}
            width="100%"
            height={getConditionalContent({
              condition: screenWidth < 768,
              trueValue: 300,
              falseValue: 400,
            })}
            dataKey1="name"
            axisLine={false}
            type="linear"
            dataKey2={getLocalizedMessage(intl, "value.label")}
            linkLabel={getLocalizedMessage(intl, "grading.link.label")}
            barColor={variables.magenta}
          />
        ) : (
          <Text
            style={{
              ...GradingGraphStyles.notFoundText,
              marginTop: getConditionalContent({
                condition: responsive.isSm,
                trueValue: "20%",
                falseValue: "40%",
              }),
            }}
          >
            {dataNotFound}
          </Text>
        )
      }
    />
  );
};

export default GradingGraph;
